import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Calendar, Clock, Edit2, CheckCircle, MessageSquarePlus, Lock, User, Search, X } from 'lucide-react';

import { BACKEND_URL } from '../utils/backendUrl';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import SearchableSelect from '@/components/ui/SearchableSelect';
import { hasPermission } from '../utils/permissions';
const API_URL = BACKEND_URL;

const initialFormData = () => ({
  task_date: new Date().toISOString().split('T')[0],
  task_description: '',
  hours_spent: '',
  status: 'logged',
  customer_id: '',
});

const DailyTasks = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { selectedFy } = useFiscalYear();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canEditAny = isAdmin || hasPermission(user, 'daily_tasks', 'edit');
  const canDeleteAny = isAdmin || hasPermission(user, 'daily_tasks', 'delete');
  const [tasks, setTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState(initialFormData());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progressDraft, setProgressDraft] = useState({}); // { [taskId]: 'note text' }
  const [busyTaskId, setBusyTaskId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | logged | in_progress | completed

  // Auth flows via httpOnly cookie set by /api/auth/login. We only need
  // credentials: 'include' on each fetch call so the cookie is attached.
  const fetchOpts = useCallback(
    (extra = {}) => ({
      credentials: 'include',
      ...extra,
      headers: { 'Content-Type': 'application/json', ...(extra.headers || {}) },
    }),
    []
  );

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/daily-tasks`, fetchOpts());
      if (response.ok) setTasks(await response.json());
    } catch (e) {
      console.error('Error fetching tasks:', e);
    }
  }, [fetchOpts]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/daily-tasks/customers`, fetchOpts());
      if (response.ok) setCustomers(await response.json());
    } catch (e) {
      console.error('Error fetching customers:', e);
    }
  }, [fetchOpts]);

  useEffect(() => {
    fetchTasks();
    fetchCustomers();
  }, [fetchTasks, fetchCustomers]);

  const safeReadError = async (response) => {
    // Defensive: if some intermediary already consumed the body, fall back to status text
    try {
      const text = await response.text();
      if (!text) return `Request failed (${response.status})`;
      try {
        const data = JSON.parse(text);
        return data.detail || `Request failed (${response.status})`;
      } catch (_) {
        return text.slice(0, 200) || `Request failed (${response.status})`;
      }
    } catch (_) {
      return `Request failed (${response.status})`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const url = editingTask
        ? `${API_URL}/api/daily-tasks/${editingTask.id}`
        : `${API_URL}/api/daily-tasks`;
      const method = editingTask ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        hours_spent: parseFloat(formData.hours_spent),
        customer_id: formData.customer_id || null,
      };

      const response = await fetch(url, fetchOpts({
        method,
        body: JSON.stringify(payload),
      }));

      if (response.ok) {
        setSuccess(editingTask ? 'Task updated successfully!' : 'Task logged successfully!');
        setShowForm(false);
        setEditingTask(null);
        setFormData(initialFormData());
        fetchTasks();
      } else {
        setError(await safeReadError(response));
      }
    } catch (err) {
      setError('Error saving task');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      task_date: task.task_date,
      task_description: task.task_description,
      hours_spent: task.hours_spent.toString(),
      status: task.status,
      customer_id: task.customer_id || '',
    });
    setShowForm(true);
  };

  // Admin-only: hard delete a daily task (works even for completed tasks)
  const handleAdminDelete = async (task) => {
    if (!window.confirm(`Delete this daily task by ${task.user_name}? This cannot be undone.`)) return;
    setBusyTaskId(task.id);
    try {
      const response = await fetch(`${API_URL}/api/daily-tasks/${task.id}`, fetchOpts({
        method: 'DELETE',
      }));
      if (response.ok) {
        setSuccess('Task deleted successfully');
        await fetchTasks();
      } else {
        setError(await safeReadError(response));
      }
    } catch (e) {
      console.error('Error deleting task:', e);
    } finally {
      setBusyTaskId(null);
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingTask(null);
    setFormData(initialFormData());
    setError('');
  };

  const submitProgress = async (taskId) => {
    const note = (progressDraft[taskId] || '').trim();
    if (!note) return;
    setBusyTaskId(taskId);
    try {
      const response = await fetch(`${API_URL}/api/daily-tasks/${taskId}/progress`, fetchOpts({
        method: 'POST',
        body: JSON.stringify({ note }),
      }));
      if (response.ok) {
        setProgressDraft((prev) => ({ ...prev, [taskId]: '' }));
        await fetchTasks();
      } else {
        setError(await safeReadError(response));
      }
    } finally {
      setBusyTaskId(null);
    }
  };

  const closeTask = async (taskId) => {
    if (!window.confirm('Mark this task as completed? Once closed, the task cannot be edited.')) return;
    setBusyTaskId(taskId);
    try {
      const response = await fetch(`${API_URL}/api/daily-tasks/${taskId}/close`, fetchOpts({
        method: 'POST',
      }));
      if (response.ok) {
        setSuccess('Task marked as completed');
        await fetchTasks();
      } else {
        setError(await safeReadError(response));
      }
    } finally {
      setBusyTaskId(null);
    }
  };

  const statusBadge = (status) => {
    const map = {
      logged: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      completed: 'bg-green-100 text-green-700',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const fyStart = selectedFy?.start_date || null;
    const fyEnd = selectedFy?.end_date || null;
    return tasks.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      // Global Fiscal Year scope — filter on task_date.
      if (fyStart || fyEnd) {
        const day = (t.task_date || '').slice(0, 10);
        if (!day) return false;
        if (fyStart && day < fyStart) return false;
        if (fyEnd && day > fyEnd) return false;
      }
      if (!q) return true;
      const haystack = [
        t.task_description,
        t.customer_name,
        t.status,
        t.user_name,
        t.task_date,
        ...(t.progress_notes || []).map((p) => p.note),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [tasks, searchQuery, statusFilter, selectedFy?.id]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-700 to-green-700 bg-clip-text text-transparent">
            Daily Tasks
          </h1>
          <p className="text-gray-600 mt-1">Log your daily work and hours</p>
        </div>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-blue-700 to-green-700"
            data-testid="log-new-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Log New Task
          </Button>
        )}
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded" data-testid="success-banner">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" data-testid="error-banner">
          {error}
        </div>
      )}

      {/* Task Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingTask ? 'Edit Task' : 'Log New Task'}</CardTitle>
            <CardDescription>Enter your daily task details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="daily-task-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Date</label>
                  <DateInputDMY value={formData.task_date}
 onChange={(e) => setFormData({ ...formData, task_date: e.target.value })}
 className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
 required
 data-testid="task-date-input"
 />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Hours Spent</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="24"
                    value={formData.hours_spent}
                    onChange={(e) => setFormData({ ...formData, hours_spent: e.target.value })}
                    placeholder="e.g., 8"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="task-hours-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Customer</label>
                <SearchableSelect
                  value={formData.customer_id}
                  onChange={(val) => setFormData({ ...formData, customer_id: val })}
                  options={customers.map((c) => ({ value: c.id, label: c.name }))}
                  placeholder="— No customer —"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Task Description</label>
                <textarea
                  value={formData.task_description}
                  onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                  placeholder="Describe what you worked on today..."
                  rows="4"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  data-testid="task-description-input"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-700 to-green-700"
                  data-testid="task-save-btn"
                >
                  {loading ? 'Saving...' : editingTask ? 'Update Task' : 'Log Task'}
                </Button>
                <Button type="button" onClick={cancelForm} variant="outline" data-testid="task-cancel-btn">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Search & Filter Bar */}
      {tasks.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3" data-testid="daily-tasks-search-bar">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by description, customer, status or progress notes..."
              className="w-full pl-9 pr-9 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="daily-tasks-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
                data-testid="daily-tasks-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="daily-tasks-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="logged">Logged</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <div className="text-xs text-gray-500 self-center sm:ml-2" data-testid="daily-tasks-result-count">
            {filteredTasks.length} of {tasks.length}
          </div>
        </div>
      )}

      {/* Tasks List */}
      <div className="space-y-4">
        {tasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No tasks logged yet</p>
              <p className="text-sm mt-2">Start logging your daily tasks to track your work</p>
            </CardContent>
          </Card>
        ) : filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500" data-testid="daily-tasks-no-match">
              <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No tasks match your search</p>
              <p className="text-sm mt-2">Try adjusting your filters or clearing the search</p>
            </CardContent>
          </Card>
        ) : (
          filteredTasks.map((task) => {
            const isClosed = task.status === 'completed';
            return (
              <Card key={task.id} className="hover:shadow-md transition-shadow" data-testid={`task-card-${task.id}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDateDMY(task.task_date)}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="w-4 h-4 mr-1" />
                          {task.hours_spent} hours
                        </div>
                        {task.customer_name && (
                          <div className="flex items-center text-sm text-gray-700" data-testid={`task-customer-${task.id}`}>
                            <User className="w-4 h-4 mr-1" />
                            {task.customer_name}
                          </div>
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full ${statusBadge(task.status)}`} data-testid={`task-status-${task.id}`}>
                          {task.status === 'in_progress' ? 'In Progress' : task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-gray-800 whitespace-pre-wrap">{task.task_description}</p>
                      <p className="text-xs text-gray-500 mt-2">Logged by: {task.user_name}</p>

                      {/* Progress Notes */}
                      {task.progress_notes && task.progress_notes.length > 0 && (
                        <div className="mt-3 border-l-2 border-blue-200 pl-3 space-y-2" data-testid={`task-progress-list-${task.id}`}>
                          <p className="text-xs font-semibold text-gray-700">Progress</p>
                          {task.progress_notes.map((p, i) => (
                            <div key={`${task.id}-pn-${i}`} className="text-sm">
                              <span className="text-xs text-gray-500 mr-2">
                                {new Date(p.timestamp).toLocaleString()}
                              </span>
                              <span className="text-gray-800">{p.note}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add progress + Close (only when not completed) */}
                      {!isClosed ? (
                        <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-2">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-600 mb-1">Add progress note</label>
                            <textarea
                              rows="2"
                              value={progressDraft[task.id] || ''}
                              onChange={(e) => setProgressDraft({ ...progressDraft, [task.id]: e.target.value })}
                              placeholder="Update on what you did since last entry..."
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              data-testid={`task-progress-input-${task.id}`}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              onClick={() => submitProgress(task.id)}
                              disabled={busyTaskId === task.id || !(progressDraft[task.id] || '').trim()}
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid={`task-add-progress-btn-${task.id}`}
                            >
                              <MessageSquarePlus className="w-4 h-4 mr-1" />
                              Add Progress
                            </Button>
                            <Button
                              type="button"
                              onClick={() => closeTask(task.id)}
                              disabled={busyTaskId === task.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`task-close-btn-${task.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Mark Complete
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 flex items-center text-sm text-gray-500" data-testid={`task-closed-indicator-${task.id}`}>
                          <Lock className="w-4 h-4 mr-1" />
                          Task is closed and locked from edits
                        </div>
                      )}
                    </div>

                    {/* Edit / Delete buttons */}
                    <div className="ml-2 shrink-0 flex flex-col gap-1">
                      {(!isClosed || canEditAny) && (
                        <Button
                          onClick={() => handleEdit(task)}
                          variant="ghost"
                          size="sm"
                          title={isClosed ? 'Admin: edit closed task' : 'Edit task'}
                          data-testid={`task-edit-btn-${task.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {canDeleteAny && (
                        <Button
                          onClick={() => handleAdminDelete(task)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Admin: delete this task"
                          data-testid={`task-delete-btn-${task.id}`}
                          disabled={busyTaskId === task.id}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DailyTasks;
